import axios from 'axios';
import { getAccessToken } from 'axios-jwt';
import CoreApi from '@/api/CoreApi';
import { BASE_URL } from '@/Constants';
import { TrainingStatus } from '@/models/RobVision';
import type {
  VisionProject,
  VisionProjectType,
  ProjectMember,
  CADModelData,
  NewCADProject,
  TrainingData,
  ProjectTrainingRun,
  Deployment,
  NewDeployment,
  ImageAnnotationData,
  MaskData,
  AdvancedSettings,
  LabelingImage,
  LabelingImageStatus,
  LabelingImportResult,
  LabelingLock,
  LabelingDataset,
  LabelingImageFiles,
  LabelingEmbeddingInfo,
  SamAssets,
  PerImageCocoDoc,
} from '@/models/RobVision';

export async function loadPartTextures(): Promise<string[]> {
  try {
    const response = await CoreApi.axiosClient.get(`/robvision/textures`);
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response.data.detail);
    throw new Error(error.response.data.detail);
  }
}

export async function loadUserProjects(
  includeArchived = false,
): Promise<VisionProject[]> {
  try {
    const response = await CoreApi.axiosClient.get(`/robvision/projects`, {
      params: { include_archived: includeArchived },
    });
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response.data.detail);
    throw new Error(error.response.data.detail);
  }
}

export async function unarchiveProject(
  visionProjectUuid: string,
): Promise<void> {
  try {
    await CoreApi.axiosClient.post(
      `/robvision/projects/${visionProjectUuid}/unarchive`,
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response?.data?.detail || error.message);
    throw new Error(error.response?.data?.detail || 'Unknown error occurred');
  }
}

export async function getProjectMembers(
  visionProjectUuid: string,
): Promise<ProjectMember[]> {
  try {
    const response = await CoreApi.axiosClient.get(
      `/robvision/projects/${visionProjectUuid}/members`,
    );
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response?.data?.detail || error.message);
    throw new Error(error.response?.data?.detail || 'Unknown error occurred');
  }
}

export async function addProjectMember(
  visionProjectUuid: string,
  email: string,
): Promise<void> {
  try {
    await CoreApi.axiosClient.post(
      `/robvision/projects/${visionProjectUuid}/members`,
      { email },
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response?.data?.detail || error.message);
    throw new Error(error.response?.data?.detail || 'Unknown error occurred');
  }
}

export async function removeProjectMember(
  visionProjectUuid: string,
  memberUuid: string,
): Promise<void> {
  try {
    await CoreApi.axiosClient.delete(
      `/robvision/projects/${visionProjectUuid}/members/${memberUuid}`,
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response?.data?.detail || error.message);
    throw new Error(error.response?.data?.detail || 'Unknown error occurred');
  }
}

export async function loadCADData(
  visionProjectUuid: string,
): Promise<CADModelData[]> {
  try {
    const response = await CoreApi.axiosClient.get(
      `/robvision/projects/cad-models/${visionProjectUuid}`,
    );
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response.data.detail);
    throw new Error(error.response.data.detail);
  }
}

export async function loadImageAnnotations(
  visionProjectUuid: string,
): Promise<ImageAnnotationData[]> {
  try {
    const response = await CoreApi.axiosClient.get(
      `/robvision/projects/image-annotation/${visionProjectUuid}`,
    );
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response?.data?.detail || error.message);
    throw new Error(
      error.response?.data?.detail || 'Failed to load image annotations',
    );
  }
}

export async function fetchMaskData(url: string): Promise<MaskData> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous'; // Required for canvas pixel access
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = url;
  });

  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('2D context not supported or canvas is null');
  }
  ctx.drawImage(img, 0, 0);
  const imageData = ctx.getImageData(0, 0, img.width, img.height);
  const grayscaleData = new Uint8Array(img.width * img.height);

  for (let i = 0; i < grayscaleData.length; i++) {
    const r = imageData.data[i * 4];
    // You can adjust thresholding here if needed
    grayscaleData[i] = r > 128 ? 255 : 0;
  }

  return {
    data: grayscaleData,
    width: img.width,
    height: img.height,
  };
}

export async function transformPartToSTL(file: File): Promise<File> {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await CoreApi.axiosClient.post(
      '/robvision/parts/transform',
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        responseType: 'blob',
      },
    );
    const responseFile = new File([response.data], 'part.stl');
    return responseFile;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response.data.detail);
    throw new Error(error.response.data.detail);
  }
}

export async function createVisionProject(
  name: string,
  visionProjectType: VisionProjectType,
  signal?: AbortSignal,
): Promise<string> {
  const formData = new FormData();
  formData.append('name', name);
  formData.append('vision_project_type', visionProjectType);
  try {
    const response = await CoreApi.axiosClient.post(
      '/robvision/projects',
      formData,
      { signal },
    );
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response?.data?.detail ?? error.message);
    throw new Error(error.response?.data?.detail ?? error.message);
  }
}

export async function createPart(
  projectUuid: string,
  newCADProject: NewCADProject,
): Promise<void> {
  try {
    const formData = new FormData();

    const isHexColor = newCADProject.texture.startsWith('#');
    const textureForBackend = isHexColor
      ? 'custom_color'
      : newCADProject.texture;
    const rgbaForBackend = isHexColor
      ? newCADProject.texture
      : newCADProject.rgba_value;

    formData.append('label', 'part');
    formData.append('texture', textureForBackend.toLowerCase());
    formData.append('rgba_value', rgbaForBackend);

    if (newCADProject.file) {
      formData.append('file', newCADProject.file);
    }

    await CoreApi.axiosClient.post(
      `/robvision/projects/cad-models/${projectUuid}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response?.data?.detail || error.message);
    throw new Error(error.response?.data?.detail || 'Unknown error occurred');
  }
}

export async function uploadImageAnnotation({
  visionProjectUuid,
  label,
  size,
  image,
  segmentationMask,
  cutout,
  signal,
}: {
  visionProjectUuid: string;
  label: string;
  size: number[]; // must be float-compatible
  image: File;
  segmentationMask: File;
  cutout: File;
  signal?: AbortSignal;
}): Promise<void> {
  const formData = new FormData();
  formData.append('label', label);

  // Append list[float] as individual entries (FastAPI handles it)
  size.forEach((val) => formData.append('size', val.toString()));

  formData.append('image', image);
  formData.append('segmentation_mask', segmentationMask);
  formData.append('cutout', cutout);

  try {
    await CoreApi.axiosClient.post(
      `/robvision/projects/image-annotation/${visionProjectUuid}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        signal,
      },
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response?.data?.detail || error.message);
    throw new Error(error.response?.data?.detail || 'Upload failed');
  }
}

export type TrainingAllowed = {
  // True when at least one model generation may be trained right now.
  allowed: boolean;
  reason: string | null;
  // The retrain entitlement is per model generation: one upload allows one
  // current-model and one deprecated-model training.
  currentModelAllowed: boolean;
  deprecatedModelAllowed: boolean;
};

export async function getTrainingAllowed(
  projectUuid: string,
): Promise<TrainingAllowed> {
  try {
    const response = await CoreApi.axiosClient.get(
      `/robvision/projects/training-allowed/${projectUuid}`,
    );
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response?.data?.detail ?? error.message);
    throw new Error(error.response?.data?.detail ?? error.message);
  }
}

export type RealWorldDataStatus = {
  realWorldData: boolean;
  sizeBytes: number | null;
};

export async function getRealWorldDataStatus(
  projectUuid: string,
): Promise<RealWorldDataStatus> {
  try {
    const response = await CoreApi.axiosClient.get(
      `/robvision/projects/real-world-data/${projectUuid}`,
    );
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response?.data?.detail ?? error.message);
    throw new Error(error.response?.data?.detail ?? error.message);
  }
}

export async function uploadCustomBackgrounds(
  projectUuid: string,
  files: File[],
  signal?: AbortSignal,
): Promise<void> {
  const formData = new FormData();
  files.forEach((file) => formData.append('images', file));
  try {
    await CoreApi.axiosClient.post(
      `/robvision/projects/custom-backgrounds/${projectUuid}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal,
      },
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response?.data?.detail ?? error.message);
    throw new Error(error.response?.data?.detail ?? error.message);
  }
}

export async function getCustomBackgrounds(
  projectUuid: string,
): Promise<string[]> {
  try {
    const response = await CoreApi.axiosClient.get(
      `/robvision/projects/custom-backgrounds/${projectUuid}`,
    );
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response?.data?.detail ?? error.message);
    throw new Error(error.response?.data?.detail ?? error.message);
  }
}

export async function deleteProject(projectUuid: string): Promise<void> {
  try {
    await CoreApi.axiosClient.delete(`/robvision/projects/${projectUuid}`);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response.data.detail);
    throw new Error(error.response.data.detail);
  }
}

export async function triggerPartTraining(
  projectUuid: string,
  options?: {
    // Train on this stored dataset version (its frozen zip is copied into
    // the training input first). Omitted = train on whatever the slot holds.
    datasetVersion?: number;
    // Display name for the run / resulting model version.
    name?: string;
    // Train the deprecated Mask R-CNN model with this rotation classifier
    // step instead of YOLOv9. Per run.
    resolutionAngle?: number;
  },
): Promise<void> {
  try {
    const body: Record<string, unknown> = {};
    if (options?.datasetVersion !== undefined) {
      body.datasetVersion = options.datasetVersion;
    }
    if (options?.name) {
      body.name = options.name;
    }
    if (options?.resolutionAngle !== undefined) {
      body.deprecatedModel = true;
      body.resolutionAngle = options.resolutionAngle;
    }
    await CoreApi.axiosClient.post(
      `/robvision/projects/trigger/${projectUuid}`,
      Object.keys(body).length > 0 ? body : undefined,
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response.data.detail);
    throw new Error(error.response.data.detail);
  }
}
export async function getProjectTraining(
  projectUuid: string,
): Promise<TrainingData> {
  try {
    const response = await CoreApi.axiosClient.get(
      `/robvision/projects/trigger/${projectUuid}`,
    );
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response.data.detail);
    throw new Error(error.response.data.detail);
  }
}

/** Stop the project's running training (two-phase cancel: CANCELING until
 * the backend confirms the termination). */
export async function cancelProjectTraining(
  projectUuid: string,
): Promise<void> {
  try {
    await CoreApi.axiosClient.delete(
      `/robvision/projects/trigger/${projectUuid}`,
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response?.data?.detail ?? error.message);
    throw new Error(error.response?.data?.detail ?? error.message);
  }
}

/** All training runs of the project, newest first (the model-versions
 * table). Falls back to a single row from the latest-run endpoint while the
 * list endpoint is not deployed yet. */
export async function listProjectTrainings(
  projectUuid: string,
): Promise<ProjectTrainingRun[]> {
  try {
    const response = await CoreApi.axiosClient.get(
      `/robvision/projects/${projectUuid}/trainings`,
    );
    return response.data ?? [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (error.response?.status !== 404 && error.response?.status !== 405) {
      console.error(error.response?.data?.detail ?? error.message);
      throw new Error(error.response?.data?.detail ?? error.message);
    }
  }
  // Endpoint missing (or no run recorded): degrade to the latest run.
  const latest = await getProjectTraining(projectUuid).catch(() => null);
  if (!latest?.runId) {
    return [];
  }
  const deployments =
    latest.status === TrainingStatus.SUCCEEDED
      ? await getProjectDeployments(projectUuid).catch(() => [])
      : [];
  return [
    {
      runId: latest.runId,
      status: latest.status,
      createdAt: '',
      baseModel: null,
      deployments,
    },
  ];
}

export async function createProjectDeployment(
  newDeployment: NewDeployment,
): Promise<void> {
  try {
    await CoreApi.axiosClient.post(
      '/robvision/projects/deployments',
      newDeployment,
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response.data.detail);
    throw new Error(error.response.data.detail);
  }
}

export async function getProjectDeployments(
  projectUuid: string,
): Promise<Deployment[]> {
  try {
    const response = await CoreApi.axiosClient.get(
      `/robvision/projects/deployments/${projectUuid}`,
    );
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response.data.detail);
    throw new Error(error.response.data.detail);
  }
}

export async function addAdvancedSettings(
  visionProjectUuid: string,
  settings: AdvancedSettings,
  signal?: AbortSignal,
): Promise<void> {
  try {
    const response = await CoreApi.axiosClient.post(
      `/robvision/projects/training-settings/${visionProjectUuid}`,
      settings,
      { signal },
    );
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response?.data?.detail ?? error.message);
    throw new Error(error.response?.data?.detail ?? error.message);
  }
}

export async function getAdvancedSettings(
  visionProjectUuid: string,
): Promise<AdvancedSettings> {
  try {
    const response = await CoreApi.axiosClient.get(
      `/robvision/projects/training-settings/${visionProjectUuid}`,
    );

    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response?.data?.detail ?? error.message);
    throw new Error(error.response?.data?.detail ?? error.message);
  }
}

// ---- Labeling tool (RobLabel) ----

export class LabelingLockHeldError extends Error {}

export async function acquireLabelingLock(
  visionProjectUuid: string,
): Promise<LabelingLock> {
  try {
    const response = await CoreApi.axiosClient.post(
      `/robvision/projects/${visionProjectUuid}/lock`,
    );
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    const detail = error.response?.data?.detail ?? error.message;
    console.error(detail);
    if (error.response?.status === 409) {
      throw new LabelingLockHeldError(detail);
    }
    throw new Error(detail);
  }
}

export async function getLabelingLock(
  visionProjectUuid: string,
): Promise<LabelingLock | null> {
  try {
    const response = await CoreApi.axiosClient.get(
      `/robvision/projects/${visionProjectUuid}/lock`,
    );
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response?.data?.detail ?? error.message);
    throw new Error(error.response?.data?.detail ?? error.message);
  }
}

export async function releaseLabelingLock(
  visionProjectUuid: string,
  options: {
    // Send via fetch keepalive so the release survives a tab/window close.
    // Takes the pre-fetched bearer token — awaiting the token store inside a
    // beforeunload handler yields to the unload and the request may never
    // be queued.
    keepalive?: boolean;
    authToken?: string | null;
  } = {},
): Promise<void> {
  const path = `/robvision/projects/${visionProjectUuid}/lock`;
  if (options.keepalive) {
    await fetch(`${BASE_URL}${path}`, {
      method: 'DELETE',
      keepalive: true,
      headers: options.authToken
        ? { Authorization: `Bearer ${options.authToken}` }
        : {},
    }).catch(() => {
      // Fire-and-forget: the page is going away; the TTL is the fallback.
    });
    return;
  }
  try {
    await CoreApi.axiosClient.delete(path);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    // Releasing is best-effort on teardown; an expired/stolen lock is fine.
    console.error(error.response?.data?.detail ?? error.message);
  }
}

export async function importLabelingImages(
  visionProjectUuid: string,
  file: File,
): Promise<LabelingImportResult> {
  const formData = new FormData();
  formData.append('file', file);
  try {
    const response = await CoreApi.axiosClient.post(
      `/robvision/projects/${visionProjectUuid}/images`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response?.data?.detail ?? error.message);
    throw new Error(error.response?.data?.detail ?? error.message);
  }
}

export async function listLabelingImages(
  visionProjectUuid: string,
): Promise<LabelingImage[]> {
  // The endpoint caps a page at 1000; page until a short page arrives so
  // projects beyond that don't silently lose images. The page cap is a
  // backstop against a backend that ignores `offset` (infinite loop).
  const pageSize = 1000;
  const maxPages = 100;
  const images: LabelingImage[] = [];
  try {
    for (let page = 0; page < maxPages; page++) {
      const response = await CoreApi.axiosClient.get(
        `/robvision/projects/${visionProjectUuid}/images`,
        { params: { limit: pageSize, offset: images.length } },
      );
      images.push(...response.data);
      if (response.data.length < pageSize) {
        return images;
      }
    }
    return images;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response?.data?.detail ?? error.message);
    throw new Error(error.response?.data?.detail ?? error.message);
  }
}

export async function getLabelingImageFiles(
  visionProjectUuid: string,
  imageUuid: string,
): Promise<LabelingImageFiles> {
  try {
    const response = await CoreApi.axiosClient.get(
      `/robvision/projects/${visionProjectUuid}/images/${imageUuid}/files`,
    );
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response?.data?.detail ?? error.message);
    throw new Error(error.response?.data?.detail ?? error.message);
  }
}

export async function patchLabelingImage(
  visionProjectUuid: string,
  imageUuid: string,
  status: LabelingImageStatus,
): Promise<LabelingImage> {
  try {
    const response = await CoreApi.axiosClient.patch(
      `/robvision/projects/${visionProjectUuid}/images/${imageUuid}`,
      { status },
    );
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response?.data?.detail ?? error.message);
    throw new Error(error.response?.data?.detail ?? error.message);
  }
}

/**
 * Save an image's annotations (per-image COCO doc). Requires holding the
 * labeling lock; the backend writes S3, computes the summary mirror and
 * moves the image to `in_progress` — or `ready` when `markReady` is set.
 * `keepalive` sends the request via fetch so it survives tab close.
 */
export async function putImageAnnotations(
  visionProjectUuid: string,
  imageUuid: string,
  doc: PerImageCocoDoc,
  options: {
    markReady?: boolean;
    keepalive?: boolean;
    // Pre-fetched bearer token for the keepalive path: awaiting the token
    // store inside a beforeunload handler yields to the unload and the
    // request may never be queued.
    authToken?: string | null;
  } = {},
): Promise<LabelingImage | null> {
  const query = options.markReady ? '?status=ready' : '';
  const path = `/robvision/projects/${visionProjectUuid}/images/${imageUuid}/annotations${query}`;
  if (options.keepalive) {
    const body = JSON.stringify(doc);
    // fetch keepalive enforces a ~64 KiB body quota and rejects larger
    // payloads outright — a COCO doc with a few polygons exceeds that.
    // Those fall through to the normal request, which may not survive a
    // tab close but at least can succeed.
    if (body.length < 60_000) {
      const token = options.authToken ?? (await getAccessToken());
      await fetch(`${BASE_URL}${path}`, {
        method: 'PUT',
        body,
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }).catch(() => {
        // Fire-and-forget: the page is going away.
      });
      return null;
    }
  }
  try {
    const response = await CoreApi.axiosClient.put(path, doc);
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    const detail = error.response?.data?.detail ?? error.message;
    console.error(detail);
    if (error.response?.status === 409) {
      // The only 409 on this endpoint is a missing/foreign labeling lock —
      // callers must treat it as "lock lost", not as a retryable failure.
      throw new LabelingLockHeldError(detail);
    }
    throw new Error(detail);
  }
}

/**
 * The project's class names (the synthetic model's classes) — the only
 * classes an annotation may use, so exported datasets stay
 * finetune-compatible.
 */
export async function getProjectClasses(
  visionProjectUuid: string,
): Promise<string[]> {
  try {
    const response = await CoreApi.axiosClient.get(
      `/robvision/projects/${visionProjectUuid}/classes`,
    );
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response?.data?.detail ?? error.message);
    throw new Error(error.response?.data?.detail ?? error.message);
  }
}

/**
 * Presigned URL for the global image-wide positional embedding (written
 * once to S3 by the embedding job, shared by all projects). `modelName`
 * identifies the encoder checkpoint and versions the browser cache.
 */
export async function getSamAssets(): Promise<SamAssets> {
  try {
    const response = await CoreApi.axiosClient.get('/robvision/sam-assets');
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response?.data?.detail ?? error.message);
    throw new Error(error.response?.data?.detail ?? error.message);
  }
}

/** Presigned embedding URL(s); null while the embedding is not ready yet. */
export async function getImageEmbedding(
  visionProjectUuid: string,
  imageUuid: string,
): Promise<LabelingEmbeddingInfo | null> {
  try {
    const response = await CoreApi.axiosClient.get(
      `/robvision/projects/${visionProjectUuid}/images/${imageUuid}/embedding`,
    );
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    console.error(error.response?.data?.detail ?? error.message);
    throw new Error(error.response?.data?.detail ?? error.message);
  }
}

export async function createLabelingDataset(
  visionProjectUuid: string,
  imageUuids: string[],
): Promise<LabelingDataset> {
  try {
    const response = await CoreApi.axiosClient.post(
      `/robvision/projects/${visionProjectUuid}/datasets`,
      { imageUuids },
    );
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response?.data?.detail ?? error.message);
    throw new Error(error.response?.data?.detail ?? error.message);
  }
}

export async function listLabelingDatasets(
  visionProjectUuid: string,
): Promise<LabelingDataset[]> {
  try {
    const response = await CoreApi.axiosClient.get(
      `/robvision/projects/${visionProjectUuid}/datasets`,
    );
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response?.data?.detail ?? error.message);
    throw new Error(error.response?.data?.detail ?? error.message);
  }
}

export async function setRotationLabeling(
  visionProjectUuid: string,
  rotationLabeling: boolean,
): Promise<void> {
  try {
    await CoreApi.axiosClient.patch(
      `/robvision/projects/${visionProjectUuid}`,
      { rotationLabeling },
    );
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error.response?.data?.detail ?? error.message);
    throw new Error(error.response?.data?.detail ?? error.message);
  }
}

// Presigned S3 access — plain axios so the API auth interceptor and base URL
// are not applied.

export async function fetchPresignedJson<T>(url: string): Promise<T> {
  const response = await axios.get(url);
  return response.data;
}

export async function fetchPresignedBinary(url: string): Promise<ArrayBuffer> {
  const response = await axios.get(url, { responseType: 'arraybuffer' });
  return response.data;
}
